import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setTimeout(() => el.classList.add('visible'), delay); obs.unobserve(el); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return ref;
}

const areas = [
  {
    name: 'Monroe County, MI',
    highlight: 'Primary Market',
    desc: 'River towns, lake communities, and suburban neighborhoods. Home to Monroe, Frenchtown Township, and Luna Pier.',
    communities: ['Monroe', 'Temperance', 'Frenchtown Twp', 'Luna Pier', 'South Rockwood', 'Carleton'],
    img: 'https://public.readdy.ai/ai/img_res/48ea13b7c43ae86ae82edc3fc8107bad.jpg',
  },
  {
    name: 'Lenawee County, MI',
    highlight: 'Active Market',
    desc: 'Quiet rural living, small-town charm, and easy access to the greater Michigan market.',
    communities: ['Adrian', 'Tecumseh', 'Blissfield', 'Morenci', 'Hudson', 'Dundee'],
    img: 'https://readdy.ai/api/search-image?query=lenawee%20county%20michigan%20small%20town%20main%20street%20historic%20buildings%20rural%20countryside%20farmland%20green%20hills%20sunny%20afternoon%20midwest%20community&width=700&height=480&seq=area-card-lenawee&orientation=landscape',
  },
  {
    name: 'Jackson County, MI',
    highlight: 'Growing Market',
    desc: 'Affordable homes, rural land, and a growing market with strong value opportunities.',
    communities: ['Jackson', 'Horton', 'Brooklyn', 'Napoleon', 'Hanover', 'Grass Lake'],
    img: 'https://readdy.ai/api/search-image?query=jackson%20michigan%20suburban%20neighborhood%20affordable%20homes%20green%20lawns%20mixed%20housing%20community%20bright%20day%20charming%20midwest%20residential%20streets&width=700&height=480&seq=area-card-jackson&orientation=landscape',
  },
  {
    name: 'Washtenaw County, MI',
    highlight: 'Ann Arbor Area',
    desc: 'Ann Arbor, Ypsilanti, and surrounding communities with top schools and vibrant neighborhoods.',
    communities: ['Ann Arbor', 'Ypsilanti', 'Saline', 'Chelsea', 'Dexter', 'Milan'],
    img: 'https://readdy.ai/api/search-image?query=ann%20arbor%20michigan%20charming%20tree%20lined%20residential%20street%20university%20town%20fall%20colors%20upscale%20homes%20warm%20sunlight%20walkable%20neighborhood&width=700&height=480&seq=area-card-washtenaw&orientation=landscape',
  },
  {
    name: 'Wayne County, MI',
    highlight: 'Metro Detroit',
    desc: 'Metro Detroit communities offering strong value, access, and a wide range of property types.',
    communities: ['Downriver Communities', 'Flat Rock', 'Gibraltar', 'Trenton', 'Woodhaven', 'Riverview'],
    img: 'https://readdy.ai/api/search-image?query=wayne%20county%20michigan%20suburban%20homes%20detroit%20metro%20area%20downriver%20well%20maintained%20neighborhoods%20brick%20houses%20clean%20streets%20residential%20community&width=700&height=480&seq=area-card-wayne&orientation=landscape',
  },
  {
    name: 'Lucas County, OH',
    highlight: 'Ohio Market',
    desc: 'Toledo and surrounding communities just across the Michigan border with diverse housing options.',
    communities: ['Toledo', 'Maumee', 'Perrysburg', 'Sylvania', 'Oregon', 'Waterville'],
    img: 'https://readdy.ai/api/search-image?query=toledo%20ohio%20lucas%20county%20downtown%20charming%20residential%20neighborhood%20brick%20homes%20tree%20lined%20streets%20midwest%20community%20pleasant%20sunny%20afternoon&width=700&height=480&seq=area-card-lucas&orientation=landscape',
  },
  {
    name: 'Wood County, OH',
    highlight: 'Ohio Market',
    desc: 'Bowling Green and surrounding rural communities with farmland, land, and residential options.',
    communities: ['Bowling Green', 'Perrysburg', 'Northwood', 'Rossford', 'Walbridge', 'Fostoria'],
    img: 'https://readdy.ai/api/search-image?query=wood%20county%20ohio%20bowling%20green%20residential%20neighborhood%20college%20town%20homes%20green%20lawns%20clean%20streets%20midwestern%20suburban%20community%20warm%20afternoon%20light&width=700&height=480&seq=area-card-wood&orientation=landscape',
  },
  {
    name: 'Hancock County, OH',
    highlight: 'Ohio Market',
    desc: 'Findlay and the surrounding area, known for affordability and a strong local economy.',
    communities: ['Findlay', 'Arlington', 'Arcadia', 'McComb', 'Benton Ridge', 'Vanlue'],
    img: 'https://readdy.ai/api/search-image?query=findlay%20ohio%20hancock%20county%20suburban%20neighborhood%20affordable%20homes%20residential%20streets%20midwest%20community%20pleasant%20warm%20sunny%20day%20charming%20homes&width=700&height=480&seq=area-card-hancock&orientation=landscape',
  },
  {
    name: 'Fulton County, OH',
    highlight: 'Ohio Market',
    desc: 'Rural communities and small towns near the Michigan border with land and residential opportunities.',
    communities: ['Wauseon', 'Swanton', 'Archbold', 'Delta', 'Fayette', 'Metamora'],
    img: 'https://readdy.ai/api/search-image?query=fulton%20county%20ohio%20rural%20landscape%20farmland%20small%20town%20homes%20barn%20countryside%20scenic%20midwest%20pastoral%20peaceful%20community%20sunny%20day&width=700&height=480&seq=area-card-fulton&orientation=landscape',
  },
];

export default function AreasPage() {
  const headingRef = useReveal(0);
  const card0 = useReveal(0); const card1 = useReveal(80); const card2 = useReveal(160);
  const card3 = useReveal(0); const card4 = useReveal(80); const card5 = useReveal(160);
  const card6 = useReveal(0); const card7 = useReveal(80); const card8 = useReveal(160);
  const cardRefs = [card0, card1, card2, card3, card4, card5, card6, card7, card8];
  const ctaRef = useReveal(0);

  return (
    <div className="min-h-screen bg-[#FAF9F7] page-enter">

      {/* Hero */}
      <section className="pt-28 sm:pt-32 pb-14 sm:pb-20 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 animate-fade-in delay-100 font-medium">Service Areas</p>
          <h1 className="font-cormorant text-4xl sm:text-6xl lg:text-7xl font-light text-[#111111] mb-6 animate-fade-in-up delay-200 leading-tight">
            Neighborhood<br /><em className="not-italic font-semibold">Guides</em>
          </h1>
          <p className="text-[#666] leading-relaxed animate-fade-in-up delay-300 max-w-xl mx-auto text-sm sm:text-base">
            I proudly serve Southeast Michigan and nearby Ohio. Here's a breakdown of each area, the communities I work in, and what makes each market unique.
          </p>
        </div>
      </section>

      {/* Area Cards */}
      <section className="py-14 sm:py-20 bg-[#FAF9F7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div ref={headingRef} className="reveal text-center mb-10 sm:mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">Where I Work</p>
            <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-[#111111]">Areas &amp; Communities</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
            {areas.map((area, i) => (
              <div key={i} ref={cardRefs[i]} className="reveal scale bg-white rounded-lg overflow-hidden hover-lift border border-gray-100 group">
                <div className="relative h-40 sm:h-48 overflow-hidden">
                  <img src={area.img} alt={area.name} className="w-full h-full object-cover object-top transition-transform duration-600 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute top-3 right-3">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#C8923A] text-white uppercase tracking-wide">{area.highlight}</span>
                  </div>
                </div>
                <div className="p-5 sm:p-6">
                  <h3 className="font-cormorant text-xl font-semibold text-[#111111] mb-2">{area.name}</h3>
                  <div className="w-7 h-0.5 bg-[#C8923A] mb-3"></div>
                  <p className="text-sm text-[#666] leading-relaxed mb-4">{area.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {area.communities.map((c, j) => (
                      <span key={j} className="text-xs bg-[#FAF9F7] text-[#555] px-2 py-1 rounded border border-gray-100">{c}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 sm:py-20 bg-[#111111]">
        <div ref={ctaRef} className="reveal max-w-4xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-white mb-6">Don't See Your Area?</h2>
          <p className="text-white/70 mb-8 sm:mb-10 max-w-xl mx-auto">I may still be able to help or connect you with someone who can. Just reach out — I'll always point you in the right direction.</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a href="tel:+15176629155" className="px-7 sm:px-8 py-3.5 sm:py-4 bg-[#C8923A] text-white font-semibold text-sm rounded-md hover:bg-[#B07B2A] transition-all whitespace-nowrap inline-flex items-center justify-center gap-2 hover-lift"><i className="ri-phone-line"></i> Call Kendrick</a>
            <Link to="/contact" className="px-7 sm:px-8 py-3.5 sm:py-4 border border-white/30 text-white font-semibold text-sm rounded-md hover:bg-white/10 transition-all whitespace-nowrap hover-lift">Send a Message</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
